import React, { useEffect, useState, useCallback, useMemo } from "react";
import GoogleMapReact from "google-map-react";
import Marker from "./Marker";
import { debounce, difference, isEqual, uniqBy } from "lodash";
import Button from "common/Button";
import { WifiTethering, MyLocation } from "@material-ui/icons";
import styled, { withTheme } from "styled-components";
import useMapsCoordinatesManager from "utils/hooks/PrepareDataHooks/useMapsCoordinatesManager";

function setTabIndex() {
  const list = document.getElementsByClassName("gm-control-active");
  for (let count in list) {
    if (list[count].title === "Vergrößern") {
      list[count].tabIndex = 9;
    }
    if (list[count].title === "Verkleinern") {
      list[count].tabIndex = 10;
    }
  }
}

const MyPositionButton = styled.button.attrs(props => ({
  tabIndex: props.tabIndex,
}))`
  border: none;
  cursor: pointer;
  user-select: none;
  overflow: hidden;
  width: 40px;
  height: 40px;
  background-color: white;
  position: absolute;
  right: 10px;
  bottom: 195px;
`;

function setEventsAtOnePoint(events, selectedEvent) {
  let tempEvents = [...events.filter(e => e.id !== selectedEvent?.id)];

  let duplicates = difference(tempEvents, uniqBy(tempEvents, "street"), "street");

  tempEvents.forEach(e => {
    e.events = [];
  });

  tempEvents.forEach(event => {
    const found = duplicates.find(el => el.street === event.street && event.id !== el.id);

    if (found) {
      if (event.events) {
        event.events = [...event.events, { ...found }];
      } else {
        event.events = [{ ...found }];
      }
    }
  });
  tempEvents = uniqBy(tempEvents, "street");

  if (!selectedEvent) {
    return tempEvents;
  }

  const selectedEventIsInList = tempEvents.find(el => el.id === selectedEvent.id);
  if (!selectedEventIsInList) {
    return [...tempEvents, selectedEvent];
  }

  return [...tempEvents.filter(e => e.id === selectedEvent.id), selectedEvent];
}

function MapPage({
  openInfo,
  selectedCompany,
  setOpenInfo,
  channels,
  filteredEvents,
  checkedOnlyFavs,
  setBounds,
  theme,
}) {
  const [selectedEvent, setSectedEvent] = useState(null);
  useEffect(() => {}, []);
  const [showInfoDialog, setShowInfoDialog] = useState(null);

  const markers = useMemo(() => setEventsAtOnePoint(filteredEvents, selectedEvent), [
    selectedEvent,
    filteredEvents,
  ]);
  const [
    setToUserPosition,
    DEFAULT_COORDINATES,
    handleApiLoaded,
    fitMapPoints,
    map,
    userPosition,
  ] = useMapsCoordinatesManager(selectedCompany);

  useEffect(() => {
    setShowInfoDialog(null);
  }, [selectedCompany]);

  const zoomToPoint = useCallback(
    (lat, lng, event) => {
      map.current.map_.panTo({
        lat: parseFloat(lat),
        lng: parseFloat(lng),
      });

      // map.current.map_.setZoom(16);
      if (event) {
        setSectedEvent(event);
      }
    },
    [map, setSectedEvent]
  );

  const getPointColor = useCallback(
    channel => {
      const ch = channels?.find(ch => ch.id === channel);
      return ch && ch.color;
    },
    [channels]
  );

  const changeBounds = useCallback(
    debounce(
      ({ bounds }) =>
        setBounds(currentBounds => (isEqual(bounds, currentBounds) ? currentBounds : bounds)),
      200
    ),
    []
  );

  return (
    <>
      {checkedOnlyFavs && (
        <div
          style={{
            position: "absolute",
            bottom: "15px",
            left: "15px",
            zIndex: 9,
          }}
        >
          <Button
            margin="0 15px 0 0"
            color={theme.primaryColor}
            iconMargin="0 5px 0 0"
            iconSize="20px"
            Icon={WifiTethering}
            background="#fff"
            text="Alle Firmenfavoriten im Anzeigebereich anzeigen"
            onClick={() => filteredEvents.length && fitMapPoints(filteredEvents)}
          />
        </div>
      )}
      <GoogleMapReact
        ref={map}
        options={{ fullscreenControl: false, clickableIcons: false }}
        bootstrapURLKeys={{ key: "AIzaSyDvXLFUSfLOZVCuK3_E3FwBn3lieP8Gt4U" }}
        defaultCenter={DEFAULT_COORDINATES.center}
        defaultZoom={DEFAULT_COORDINATES.zoom}
        onChange={changeBounds}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
        onTilesLoaded={() => setTimeout(() => setTabIndex(), 1000)}
      >
        {markers.map((event, index) => {
          const color = getPointColor(event.channel);
          return (
            <Marker
              openInfo={openInfo}
              setOpenInfo={setOpenInfo}
              key={index}
              lat={event.coordinates.latitude}
              lng={event.coordinates.longitude}
              zoomToPoint={() =>
                zoomToPoint(event.coordinates.latitude, event.coordinates.longitude, event)
              }
              markerColor={color}
              id={event.id}
              events={event.events}
              infoDialogData={{
                name: event.name,
                description: event.description,
              }}
              showInfoDialog={showInfoDialog}
              setShowInfoDialog={setShowInfoDialog}
            />
          );
        })}
        {selectedCompany && selectedCompany.lat && selectedCompany.lon && (
          // company marker
          <Marker
            lat={selectedCompany.lat}
            lng={selectedCompany.lon}
            openInfo={openInfo}
            setOpenInfo={setOpenInfo}
            showInfoDialog={showInfoDialog}
            setShowInfoDialog={setShowInfoDialog}
            zoomToPoint={() => zoomToPoint(selectedCompany.lat, selectedCompany.lon, null)}
            markerColor="999" //gray color for company
            id={selectedCompany.id}
            selectedCompany={selectedCompany}
            infoDialogData={{
              name: selectedCompany.co_name,
              description: "",
            }}
            channels={channels}
          />
        )}
        {userPosition?.latitude && userPosition?.longitude ? (
          <Marker lat={userPosition.latitude} lng={userPosition.longitude} userMarker />
        ) : null}
      </GoogleMapReact>
      <MyPositionButton
        type="button"
        onClick={setToUserPosition}
        title="Zurück zu meinem Standort"
        tabIndex={8}
      >
        <MyLocation />
      </MyPositionButton>
    </>
  );
}

export default withTheme(MapPage);
